import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import {
  fetchPages,
  createPage,
  updatePage,
  deletePage,
  togglePageStatus,
  resetSuccess,
} from '../../store/slices/cmsSlice';
import { DataTable, Modal, ConfirmDialog } from '../../components/Common';
import { formatDate } from '../../utils/helpers';

const PAGE_TYPES = [
  { value: 'privacy-policy', label: 'Privacy Policy' },
  { value: 'terms-conditions', label: 'Terms & Conditions' },
  { value: 'about-us', label: 'About Us' },
  { value: 'faq', label: 'FAQ' },
  { value: 'other', label: 'Other' },
];

const CmsPages = () => {
  const dispatch = useDispatch();
  const { pages, isLoading, success, error } = useSelector((state) => state.cms);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    contentType: 'html',
    pageType: 'other',
    metaTitle: '',
    metaDescription: '',
  });

  useEffect(() => {
    dispatch(fetchPages());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success('Operation successful');
      dispatch(resetSuccess());
      setIsModalOpen(false);
      setSelectedPage(null);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleOpenModal = (page = null) => {
    if (page) {
      setSelectedPage(page);
      setFormData({
        title: page.title,
        slug: page.slug,
        content: page.content || '',
        contentType: page.contentType || 'html',
        pageType: page.pageType,
        metaTitle: page.metaTitle || '',
        metaDescription: page.metaDescription || '',
      });
    } else {
      setSelectedPage(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        contentType: 'html',
        pageType: 'other',
        metaTitle: '',
        metaDescription: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPage) {
      dispatch(updatePage({ id: selectedPage._id, data: formData }));
    } else {
      dispatch(createPage(formData));
    }
  };

  const handleDelete = () => {
    if (selectedPage) {
      dispatch(deletePage(selectedPage._id));
      setIsDeleteOpen(false);
      setSelectedPage(null);
    }
  };

  const handleToggleStatus = (page) => {
    dispatch(togglePageStatus(page._id));
  };

  const columns = [
    { header: 'Title', render: (row) => row.title },
    { header: 'Slug', render: (row) => row.slug },
    {
      header: 'Type',
      render: (row) => PAGE_TYPES.find((t) => t.value === row.pageType)?.label || row.pageType,
    },
    { header: 'Version', render: (row) => `v${row.version || 1}` },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${row.isActive ? 'badge-success' : 'badge-secondary'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { header: 'Updated', render: (row) => formatDate(row.updatedAt) },
    {
      header: 'Actions',
      render: (row) => (
        <div className="table-actions">
          <button className="btn btn-secondary btn-icon" onClick={() => handleOpenModal(row)}>
            <FiEdit />
          </button>
          <button
            className={`btn btn-icon ${row.isActive ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => handleToggleStatus(row)}
          >
            {row.isActive ? <FiToggleRight /> : <FiToggleLeft />}
          </button>
          <button
            className="btn btn-danger btn-icon"
            onClick={() => {
              setSelectedPage(row);
              setIsDeleteOpen(true);
            }}
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">CMS Pages</h3>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <FiPlus /> Add Page
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable columns={columns} data={pages} isLoading={isLoading} />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedPage ? 'Edit Page' : 'Add Page'}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Slug *</label>
              <input
                type="text"
                className="form-control"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })
                }
                required
              />
            </div>
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Page Type *</label>
              <select
                className="form-control form-select"
                value={formData.pageType}
                onChange={(e) => setFormData({ ...formData, pageType: e.target.value })}
              >
                {PAGE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Content Type</label>
              <select
                className="form-control form-select"
                value={formData.contentType}
                onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
              >
                <option value="html">HTML</option>
                <option value="text">Plain Text</option>
                <option value="markdown">Markdown</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Content * (HTML Format)</label>
            <textarea
              className="form-control"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={12}
              required
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Meta Title</label>
              <input
                type="text"
                className="form-control"
                value={formData.metaTitle}
                onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Meta Description</label>
              <input
                type="text"
                className="form-control"
                value={formData.metaDescription}
                onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Page"
        message={`Are you sure you want to delete "${selectedPage?.title}"?`}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CmsPages;
